export interface PhotoTransform {
  crop: CropArea;
  zoom: number;
  cropPosition: CropPosition;
}

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CropPosition {
  x: number;
  y: number;
}

export interface PhotoWithTransform {
  url: string;
  transform: PhotoTransform | null;
}

export interface NormalizedPhoto {
  url: string | null;
  transform: PhotoTransform | null;
}

export interface BackgroundStyles {
  backgroundSize: string;
  backgroundPosition: string;
}

type PhotoInput = string | PhotoWithTransform | null | undefined;

/**
 * Normalizes photo data to a consistent format.
 * Handles both legacy string URLs and new object format with transforms.
 */
export function normalizePhoto(photo: PhotoInput): NormalizedPhoto {
  if (!photo) {
    return { url: null, transform: null };
  }

  if (typeof photo === "string") {
    return { url: photo, transform: null };
  }

  return {
    url: photo.url || null,
    transform: photo.transform || null,
  };
}

/**
 * Strips transforms from all photos in a photos object.
 * Used when layout changes and transforms are no longer valid.
 */
export function stripPhotoTransforms(
  photos: Record<string, PhotoInput> | null | undefined,
): Record<string, string> {
  if (!photos) return {};

  const result: Record<string, string> = {};
  for (const key of Object.keys(photos)) {
    const { url } = normalizePhoto(photos[key]);
    if (url) {
      result[key] = url;
    }
  }
  return result;
}

/**
 * Creates a photo object with transform data.
 * Returns simple string URL if no transforms provided.
 */
export function createPhotoWithTransform(
  url: string,
  transform: PhotoTransform | null,
): string | PhotoWithTransform {
  if (!transform) {
    return url;
  }

  return { url, transform };
}

/**
 * Converts react-easy-crop output to our transform format.
 */
export function cropToTransform(
  croppedArea: CropArea,
  zoom: number,
  cropPosition: CropPosition,
): PhotoTransform {
  return {
    crop: {
      x: croppedArea.x,
      y: croppedArea.y,
      width: croppedArea.width,
      height: croppedArea.height,
    },
    zoom,
    cropPosition,
  };
}

/**
 * Computes CSS background styles to render a transformed image.
 *
 * The crop object from react-easy-crop contains:
 * - x, y: top-left corner of visible area as % of original image
 * - width, height: size of visible area as % of original image
 */
export function getBackgroundTransformStyles(transform: PhotoTransform | null): BackgroundStyles {
  if (!transform || !transform.crop) {
    return {
      backgroundSize: "cover",
      backgroundPosition: "center center",
    };
  }

  const { crop } = transform;

  // Handle edge case: full image (no actual crop)
  if (crop.width >= 99.9 && crop.height >= 99.9) {
    return {
      backgroundSize: "cover",
      backgroundPosition: "center center",
    };
  }

  // Calculate separate scales for X and Y to exactly fit the crop area
  const scaleX = 100 / crop.width;
  const scaleY = 100 / crop.height;

  // Calculate crop center in image coordinates (0-100%)
  const cropCenterX = crop.x + crop.width / 2;
  const cropCenterY = crop.y + crop.height / 2;

  // Calculate background-position for each axis
  // Formula: position = (containerCenter - cropCenter * scale) / (1 - scale)
  const posX = scaleX === 1 ? 50 : (50 - cropCenterX * scaleX) / (1 - scaleX);
  const posY = scaleY === 1 ? 50 : (50 - cropCenterY * scaleY) / (1 - scaleY);

  return {
    backgroundSize: `${scaleX * 100}% ${scaleY * 100}%`,
    backgroundPosition: `${posX}% ${posY}%`,
  };
}
